<template>
	<div>
		<carrier-list-modal v-if="showCarrierList" :tenant-id="searchParams.selectedTenant" :carriers="carriers" />
		<div class="stage">
			<page-header title="Tariffs" />

			<filter-bar
				:show-filters="showFilters"
				:has-export="true"
				:show-add-button="$global.hasUserRole(userRoles, 'tariffs:create')"
				add-button-label="Add Tariff"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@export="exportToCsv"
				@add-new="routeTo('/tariffs/new')"
			>
				<template #additional-buttons>
					<li>
						<a class="jmiBtn green withIcon" @click="listCarriers()">
							<span class="fa-light fa-satellite"></span>Show Carrier ID List
						</a>
					</li>
					<li v-if="searchParams.selectedTenant || tenants?.length === 1">
						<file-upload-button
							:disabled="!(searchParams.selectedTenant || tenants?.length === 1)"
							:tenant-id="searchParams.selectedTenant || (tenants?.length === 1 ? tenants[0].id : null)"
							:title="'Import'"
							:disabled-title="'Select a tenant to import'"
							@file-selected="fileUpload"
						/>
					</li>
					<li v-if="$global.hasUserRole(userRoles, 'reseller')">
						<file-upload-button
							:title="'Import Reseller Tariff'"
							:reseller="true"
							@file-selected="fileUpload"
						/>
					</li>
				</template>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:tenants="tenants"
				date-label="Date Added"
				show-tenant-filter
				show-date-filter
				all-tenants-label="All Tariffs"
				show-search-filter
				search-label="Name"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@tenant-change="$global.setSelectedTenant"
			/>

			<data-view
				:view-mode="'table'"
				:columns="columns"
				:sort-column="sortColumn"
				:reverse="reverse"
				:loading="loading"
				:label="'Tariffs'"
				:total-items="totalTariffs"
				:page-size="pageSize"
				:current-page="currentPage"
				:pages="pages"
				@sort="setSort"
				@update:page-size="changePageSize"
				@page-change="changePage"
			>
				<template #table-view>
					<generic-table
						:items="tariffs"
						:columns="columns"
						item-type="tariffs"
						base-route="tariffs"
						:can-edit="$global.hasUserRole(userRoles, 'tariffs:update')"
						:view-mode="'table'"
						:empty-state-config="{
							icon: 'fa-light fa-money-bill-transfer',
							title: 'No tariffs yet',
							description: 'Create your first tariff to set up pricing for call destinations.',
							action: $global.hasUserRole(userRoles, 'tariffs:create')
								? {
										label: 'Create Tariff',
										icon: 'fa-light fa-plus',
										handler: () => routeTo('/tariffs/new')
									}
								: null
						}"
						:actions="[
							{
								handler: (tariff) => routeTo(`/tariffs/${tariff.id}`),
								icon: 'fa-light fa-pencil',
								label: 'Edit'
							},
							{
								handler: exportTariffToCsv,
								icon: 'fa-light fa-download',
								label: 'Export'
							},
							{
								permission: 'tariffs:delete',
								handler: deleteTariff,
								icon: 'fa-light fa-delete-left',
								label: 'Delete'
							}
						]"
					>
						<template #owner="{ item }">
							{{
								$global.hasUserRole(userRoles, 'reseller') || $global.hasUserRole(userRoles, 'admin')
									? item.tenant
										? 'Tenant - '
										: 'Reseller - '
									: ''
							}}
							{{
								item.tenant
									? item.tenant.companyName
									: item.reseller
										? item.reseller.companyName
										: 'Unassigned'
							}}
						</template>
						<template #mobile-view="{ item }">
							<div class="flex w-full flex-col">
								<div class="text-md absolute right-4 top-4 block max-w-[160px]">
									<p class="text-right font-semibold uppercase text-gray-400">Owner</p>
									<p class="truncate text-right">
										{{
											$global.hasUserRole(userRoles, 'reseller') ||
											$global.hasUserRole(userRoles, 'admin')
												? item.tenant
													? 'Tenant - '
													: 'Reseller - '
												: ''
										}}
										{{
											item.tenant
												? item.tenant.companyName
												: item.reseller
													? item.reseller.companyName
													: 'Unassigned'
										}}
									</p>
								</div>

								<div class="flex flex-col gap-2">
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Name:</p>
										<p>{{ item.name }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Destinations:</p>
										<p>{{ item.tariffEntries || '0' }}</p>
									</div>
									<div class="flex flex-col">
										<p class="font-semibold uppercase text-gray-500">Created:</p>
										<p>{{ $global.dateFormat(item.createdAt) }}</p>
									</div>
								</div>
							</div>
						</template>
					</generic-table>
				</template>
			</data-view>
		</div>
	</div>
</template>

<script>
import tariffsApi from '../scripts/tariffs/tariffs.api'
import carriersApi from '../scripts/carriers/carriers.api'
import { router } from '../router'
import moment from 'moment'
import axios from 'axios'
import { computed } from 'vue'
import debounce from 'lodash.debounce'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	provide() {
		return {
			showCarrierList: computed({
				get: () => this.showCarrierList,
				set: (val) => (this.showCarrierList = val)
			})
		}
	},
	data() {
		return {
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-4 col-md-3 col-lg-3'
				},
				{
					key: 'owner',
					label: 'Owner',
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'tariffEntries',
					label: 'Destinations',
					class: 'col-xs-12 col-md-2 col-lg-2 hideTablet'
				},
				{
					key: 'createdAt',
					label: 'Created At',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				},
				{
					key: 'updatedAt',
					label: 'Updated At',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2'
				}
			],
			showFilters: true,
			showCarrierList: false,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			},
			tariffs: [],
			carriers: [],
			totalTariffs: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'name',
			reverse: false,
			loading: false,
			searchField: ''
		}
	},

	computed: {
		pages() {
			let numPages = Math.ceil(this.totalTariffs / this.pageSize) || 1
			return Array.apply(null, { length: numPages }).map(Number.call, Number)
		},
		selectedTenantName() {
			if (this.searchParams.selectedTenant) {
				let tenant = this.tenants.find((t) => t.id === this.searchParams.selectedTenant)
				if (tenant) return tenant.companyName
			}
			return 'All Tenants'
		}
	},

	watch: {
		searchParams: {
			handler(newValue, oldValue) {
				this.reloadTariffs()
			},
			deep: true
		},
		searchField(...args) {
			this.debouncedWatch(...args)
		},
		currentPage: {
			handler(newValue, oldValue) {
				this.reloadTariffs()
			}
		},
		'$route.query.globalSearch': {
			handler(newValue, oldValue) {
				if (newValue) this.reloadTariffs(newValue)
			}
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) this.searchParams.selectedTenant = this.$global.getSelectedTenant()

		this.debouncedWatch = debounce((newValue, oldValue) => {
			this.reloadTariffs()
		}, 1000)
		if (this.$route.query && this.$route.query.globalSearch) this.reloadTariffs(this.$route.query.globalSearch)
		else this.reloadTariffs()

		carriersApi
			.getAll()
			.then((response) => {
				this.carriers = response.data.results
			})
			.catch((error) => {
				console.log(error)
			})
	},
	beforeUnmount() {
		this.debouncedWatch.cancel()
		if (this.getRefreshInterval) clearInterval(this.getRefreshInterval)
	},

	methods: {
		formatDates(date1, date2) {
			if (date1 && date2) return moment(date1).format('Do MMM YY') + ' to ' + moment(date2).format('Do MMM YY')
			else if (date1) return moment(date1).format('Do MMM YY')
			else return null
		},
		exportToCsv() {
			let searchParams = this.getSearchParams()

			searchParams.exportCsv = true

			axios
				.get(window.dtServerUrl + '/tariffs', {
					params: searchParams
				})
				.then((response) => {
					let blob = new Blob([response.data], { type: response.headers.get('content-type') })
					// let filename = (response.headers['Content-Disposition'] || '').split('filename=')[1]
					let link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = 'teleflow-tariffs.csv'
					link.click()
				})
				.catch((error) => {
					console.log(error)
				})
		},
		listCarriers() {
			this.showCarrierList = true
		},
		deleteTariff(tariff) {
			if (tariff) {
				this.$swal({
					title: 'Delete Tariff',
					text: `Would you like to delete tariff '${tariff.name}'?`,
					showCancelButton: true,
					confirmButtonText: 'Delete',
					cancelButtonText: 'Cancel',
					showDenyButton: false,
					denyButtonText: 'Cancel'
				})
					.then((result) => {
						if (result.isConfirmed) {
							tariffsApi
								.delete(tariff.id)
								.then((response) => {
									if (response && response.data && response.data.success) this.reloadTariffs()
									else {
										this.$swal({
											title: 'Unable to remove tariff',
											icon: 'error',
											confirmButtonText: 'Confirm',
											text:
												response?.data?.error ||
												'Unknown error occurred, please contact support.'
										})
									}
								})
								.catch(console.error)
						}
					})
					.catch(console.error)
			}
		},
		setSort(column, reverse) {
			if (this.sortColumn !== column) this.reverse = false
			else this.reverse = reverse

			this.sortColumn = column

			this.reloadTariffs()
		},
		changePageSize(size) {
			this.pageSize = size
			let numPages = Math.ceil(this.totalTariffs / size) || 1
			if (numPages < this.currentPage) this.currentPage = numPages
			else this.reloadTariffs()
		},
		toggleSelectAllTariffs() {
			this.allSelected = !this.allSelected

			for (let i = 0; i < this.tariffs.length; i++) {
				this.tariffs[i].selected = this.allSelected
			}
		},
		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: ''
			}
		},
		getSearchParams() {
			let params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {}
			}

			if (this.searchParams) {
				params.where.createdAtFrom = this.searchParams.dateAddedFrom
					? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 00:00:00'
					: null
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD') + ' 23:59:59'
					: this.searchParams.dateAddedFrom
						? moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD') + ' 23:59:59'
						: null
			}

			if (this.searchParams.selectedTenant) params.where.selectedTenant = this.searchParams.selectedTenant

			if (!this.sortColumn) this.sortColumn = 'createdAt'

			params.order = {
				[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
			}

			return params
		},
		reloadTariffs(globalSearch) {
			this.loading = true
			let params = this.getSearchParams()

			if (globalSearch) {
				params = { ...params, searchTerm: globalSearch }
			} else if (this.searchField) {
				params.searchTerm = this.searchField
			}

			tariffsApi
				.getAll(params)
				.then((response) => {
					this.totalTariffs = response.data.count
					this.tariffs = response.data.results

					const maxPages = Math.ceil(this.totalTariffs / this.pageSize) || 1
					if (this.currentPage > maxPages) {
						this.currentPage = 1
						this.reloadTariffs(globalSearch)
						return
					}

					this.loading = false
				})
				.catch((err) => {
					console.error(err)
					this.loading = false
				})
		},
		fileUpload(event) {
			if (event && event.target && event.target.files && event.target.files.length) {
				let filename = event.target.files[0].name
				let formData = new FormData(document.forms[0])
				let reseller = formData.get('reseller')
				let tenantId = formData.get('tenantId')
				let tariff = this.tariffs.find(
					(t) =>
						t.name === filename.replace('.csv', '') &&
						(reseller ? t.reseller : t.tenant.id === parseInt(tenantId))
				)
				if (tariff) {
					this.$swal({
						title: 'Import Tariff',
						text: `Would you like to overwrite the tariff with the same name (${tariff.name})?`,
						showCancelButton: true,
						confirmButtonText: 'Yes, overwrite',
						cancelButtonText: 'No, create new',
						showDenyButton: true,
						denyButtonText: 'Cancel'
					}).then((result) => {
						if (result.isDismissed) {
							formData.set('name', 'Copy of ' + filename.replace('.csv', ''))
							this.uploadNewTariff(formData)
						} else if (result.isConfirmed) this.uploadReplaceTariff(formData, tariff)
					})
				} else this.uploadNewTariff(formData)

				event.target.value = null
			}
		},
		uploadNewTariff(formData) {
			this.loading = true
			axios
				.post(window.dtServerUrl + `/tariffs/upload`, formData)
				.then((result) => {
					this.reloadTariffs()
				})
				.catch((e) => {
					console.error(e)
					this.loading = false
				})
		},
		uploadReplaceTariff(formData, tariff) {
			this.loading = true
			axios
				.post(window.dtServerUrl + `/tariffs/${tariff.id}/upload`, formData)
				.then((result) => {
					this.reloadTariffs()
				})
				.catch((e) => {
					console.error(e)
					this.loading = false
				})
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		clearSelectedTenant() {
			this.searchParams.selectedTenant = ''
			this.$global.setSelectedTenant({ target: { value: '' } })
		},
		addFilterPill(name, category) {
			const pill = { name, category }
			this.pills.push(pill)
		},

		updatePills() {
			this.pills = []

			if (this.searchField) {
				this.addFilterPill(this.searchField, 'Name')
			}

			if (this.searchParams.selectedTenant) {
				this.addFilterPill(this.selectedTenantName, 'Tenant')
			}
		},
		removeFilterPill(pill) {
			const index = this.pills.findIndex((p) => p.name === pill.name && p.category === pill.category)
			if (index > -1) {
				this.pills.splice(index, 1)
			}

			switch (pill.category) {
				case 'Name':
					this.searchField = ''
					break
				case 'Tenant':
					this.searchParams.selectedTenant = ''
					break
			}

			this.reloadAll()
		},
		toggleViewMode() {
			this.viewMode = this.viewMode === 'card' ? 'table' : 'card'
		},
		changePage(page) {
			this.currentPage = page
			this.reloadTariffs()
		},
		exportTariffToCsv(tariff) {
			axios
				.get(window.dtServerUrl + '/tariffs/' + tariff.id, {
					params: { exportCsv: true }
				})
				.then((response) => {
					const blob = new Blob([response.data], { type: response.headers.get('content-type') })
					const link = document.createElement('a')
					link.href = window.URL.createObjectURL(blob)
					link.download = tariff.name + '.csv'
					link.click()
				})
				.catch(console.error)
		}
	}
}
</script>
